import { Box, CardBody as ChakraCardBody, ChakraProps, Flex, Text } from '@chakra-ui/react';
import { isValidElement } from 'react';

import Button from '@/components/atomic/v1/button/Button';
import Heading from '@/components/atomic/v1/heading/Heading';
import Link from '@/components/atomic/v1/link/Link';

import { CommonCardProps } from './cardTheme';

interface CardBodyProps extends ChakraProps {
  eyebrowText: CommonCardProps['eyebrowText'];
  bodyContent: CommonCardProps['bodyContent'];
  headerText: CommonCardProps['headerText'];
  primaryBtnText: CommonCardProps['primaryBtnText'];
  primaryBtnUrl: CommonCardProps['primaryBtnUrl'];
  secondaryBtnText: CommonCardProps['secondaryBtnText'];
  secondaryBtnUrl: CommonCardProps['secondaryBtnUrl'];
  secondaryBtnRightIconName?: CommonCardProps['secondaryBtnRightIconName'];
  secondaryBtnSize?: CommonCardProps['secondaryBtnSize'];
  footerLinkText: CommonCardProps['footerLinkText'];
  footerLinkUrl: CommonCardProps['footerLinkUrl'];
  btnColor: CommonCardProps['btnColor'];
  isExternalSecondaryBtn?: boolean;
}

const CardBody = (props: CardBodyProps) => {
  const {
    headerText,
    bodyContent,
    eyebrowText,
    footerLinkText,
    footerLinkUrl,
    primaryBtnText,
    primaryBtnUrl,
    secondaryBtnText,
    secondaryBtnUrl,
    secondaryBtnRightIconName,
    secondaryBtnSize = 'regular',
    btnColor = 'blue',
    isExternalSecondaryBtn = false,
    ...restProps
  } = props;
  const hasFooterLink = !!footerLinkText && !!footerLinkUrl;
  const hasPrimaryBtn = primaryBtnText && primaryBtnUrl;
  const hasSecondaryBtn = secondaryBtnText && secondaryBtnUrl;

  return (
    <ChakraCardBody {...restProps}>
      {eyebrowText &&
        (isValidElement(eyebrowText) ? (
          <Box display="flex" alignItems="center" gap={2} textStyle="heading.allCapS" color="text/eyebrow">
            {eyebrowText}
          </Box>
        ) : (
          <Text textStyle="heading.allCapS" color="text/eyebrow">
            {eyebrowText}
          </Text>
        ))}
      {headerText && (
        <Heading as="h4" styleType="m">
          {headerText}
        </Heading>
      )}
      {bodyContent && (
        <Box fontSize="regular" display="flex" flexDirection="column" height="100%" justifyContent="space-between">
          {bodyContent}
        </Box>
      )}
      {(hasPrimaryBtn || hasSecondaryBtn) && (
        <Flex flexWrap="wrap" gap={4} flexDirection={{ base: 'column', md: 'row' }}>
          {hasPrimaryBtn && (
            <Button href={primaryBtnUrl} color={btnColor}>
              {primaryBtnText}
            </Button>
          )}
          {hasSecondaryBtn && (
            <Button
              href={secondaryBtnUrl}
              variant="secondary"
              color={btnColor}
              rightIconName={secondaryBtnRightIconName}
              size={secondaryBtnSize}
              isExternal={isExternalSecondaryBtn}
            >
              {secondaryBtnText}
            </Button>
          )}
        </Flex>
      )}
      {hasFooterLink && (
        <Link
          w="fit-content"
          icon="arrow-right"
          mx={{ base: 'auto', md: 'unset' }}
          href={footerLinkUrl}
          fontSize="regular"
        >
          {footerLinkText}
        </Link>
      )}
    </ChakraCardBody>
  );
};

export { CardBody };
